/**
 * Between a signed-in sysadmin and the console: Google Authenticator.
 *
 * Not enrolled → a QR code to scan and a first code to confirm it.
 * Enrolled, not verified this session → a code.
 * Verified → the console.
 *
 * The gate is presentation; the backend refuses every sysadmin call until
 * the code has been verified, whatever the page shows.
 */

import { useEffect, useId, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../../auth/useAuth';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { useMfaBeginEnroll, useMfaConfirmEnroll, useMfaStatus, useMfaVerify } from './useMfa';
import own from './Console.module.css';
import form from '../../shared/Form.module.css';

export const CODE_LENGTH = 6;

function message(e: unknown): string {
  return isCallFailure(e) ? e.message : e instanceof Error ? e.message : 'That did not work.';
}

/** Six digits, and nothing else, whatever was pasted. */
export function cleanCode(v: string): string {
  return v.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

export function MfaGate({ children, compact = false }: { children: React.ReactNode; compact?: boolean }) {
  const Wrap = compact ? 'div' : 'main';
  const { session, signOut } = useAuth();
  const status = useMfaStatus(Boolean(session));

  if (status.isLoading) return <Spinner label="Checking your second factor" />;
  if (status.error) {
    return (
      <Wrap className={compact ? own.compactGate : own.signInPage}>
        <div className={own.error} role="alert">{message(status.error)}</div>
        <button type="button" className={own.plainBtn} onClick={() => void signOut()}>Sign out</button>
      </Wrap>
    );
  }
  const s = status.data;
  if (!s) return null;
  if (s.verified) return <>{children}</>;
  return s.enrolled ? <Verify lockedUntil={s.lockedUntil} compact={compact} /> : <Enroll compact={compact} />;
}

function CodeField({ id, value, onChange, disabled, describedBy }: { id: string; value: string; onChange: (v: string) => void; disabled: boolean; describedBy?: string }) {
  return (
    <input
      id={id}
      className={form.input}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={CODE_LENGTH}
      value={value}
      onChange={(e) => onChange(cleanCode(e.target.value))}
      disabled={disabled}
      aria-describedby={describedBy}
      autoFocus
      style={{ display: 'block', margin: '0 auto', fontSize: 22, letterSpacing: '0.3em', textAlign: 'center', maxWidth: 220 }}
    />
  );
}

function Enroll({ compact = false }: { compact?: boolean }) {
  const Wrap = compact ? 'div' : 'main';
  const { session, signOut } = useAuth();
  const begin = useMfaBeginEnroll();
  const confirm = useMfaConfirmEnroll();
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string | null>(null);
  const id = useId();

  useEffect(() => { begin.mutate(); /* once */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!begin.data) return;
    QRCode.toDataURL(begin.data.otpauthUrl, { margin: 1, width: 200 }).then(setQr).catch(() => setQr(null));
  }, [begin.data]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (code.length === CODE_LENGTH) confirm.mutate(code);
  }

  return (
    <Wrap className={compact ? own.compactGate : own.signInPage}>
      <div className={own.signInCard}>
        <h1 className={own.signInTitle}>Set up Google Authenticator</h1>
        <p className={own.signInBody}>
          {session?.platformSysAdmin
            ? "A system administrator account reaches every customer's data, so it needs a second factor."
            : "As your organization's System Admin you can add staff and set up sign-in, so your account needs a second factor."}{' '}
          Open Google Authenticator on your phone, add an account, and scan this code.
        </p>
        {begin.error && <div className={own.error} role="alert">{message(begin.error)}</div>}
        {begin.isPending && <Spinner label="Preparing the code" />}
        {begin.data && (
          <>
            {qr ? (
              <img src={qr} width={200} height={200} alt="QR code for Google Authenticator" style={{ display: 'block', margin: '0 auto var(--sp-4)' }} />
            ) : (
              <p className={own.signInBody}>Enter this key in the app by hand:</p>
            )}
            <p className={own.signInBody} style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
              <span className="vh">Manual key: </span>{begin.data.secret}
            </p>
            <form onSubmit={submit}>
              <label className={form.label} htmlFor={id}>Enter the six-digit code the app shows</label>
              <CodeField id={id} value={code} onChange={setCode} disabled={confirm.isPending} describedBy={confirm.error ? `${id}-err` : undefined} />
              {confirm.error && <p className={form.error} id={`${id}-err`} role="alert">{message(confirm.error)}</p>}
              <div style={{ marginTop: 'var(--sp-4)' }}>
                <button type="submit" className={`${own.googleBtn} ${own.primaryWhenReady}`} disabled={confirm.isPending || code.length !== CODE_LENGTH}>
                  {confirm.isPending ? 'Checking…' : 'Turn on the second factor'}
                </button>
              </div>
            </form>
          </>
        )}
        <p className={own.signInBody} style={{ marginTop: 'var(--sp-5)' }}>
          Signed in as {session?.email}.{' '}
          <button type="button" className={own.plainBtn} onClick={() => void signOut()}>Sign out</button>
        </p>
      </div>
    </Wrap>
  );
}

function Verify({ lockedUntil, compact = false }: { lockedUntil: number | null ; compact?: boolean }) {
  const Wrap = compact ? 'div' : 'main';
  const { session, signOut } = useAuth();
  const verify = useMfaVerify();
  const [code, setCode] = useState('');
  const id = useId();
  // The time is read when the lock is first seen and again when it runs out,
  // not on every render: a render must not depend on the clock.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (lockedUntil === null || lockedUntil <= now) return undefined;
    const t = window.setTimeout(() => setNow(Date.now()), lockedUntil - now + 50);
    return () => window.clearTimeout(t);
  }, [lockedUntil, now]);
  const locked = lockedUntil !== null && lockedUntil > now;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (code.length === CODE_LENGTH) verify.mutate(code, { onError: () => setCode('') });
  }

  return (
    <Wrap className={compact ? own.compactGate : own.signInPage}>
      <div className={own.signInCard}>
        <h1 className={own.signInTitle}>Enter your code</h1>
        <p className={own.signInBody}>
          The six digits Google Authenticator shows for StandingDocs.
          <br />
          Asked once per sign-in, on the console and on the main site.
        </p>
        {locked && <div className={own.error} role="alert">Too many wrong codes. Try again after {new Date(lockedUntil!).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.</div>}
        <form onSubmit={submit}>
          <label className="vh" htmlFor={id}>Six-digit code</label>
          <CodeField id={id} value={code} onChange={setCode} disabled={verify.isPending || locked} describedBy={verify.error ? `${id}-err` : undefined} />
          {verify.error && <p className={form.error} id={`${id}-err`} role="alert">{message(verify.error)}</p>}
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <button type="submit" className={`${own.googleBtn} ${own.primaryWhenReady}`} disabled={verify.isPending || locked || code.length !== CODE_LENGTH}>
              {verify.isPending ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </form>
        <p className={own.signInBody} style={{ marginTop: 'var(--sp-5)' }}>
          Signed in as {session?.email}.{' '}
          <button type="button" className={own.plainBtn} onClick={() => void signOut()}>Sign out</button>
        </p>
      </div>
    </Wrap>
  );
}
